"use client";

import { TCandidatesConfig } from "@/app/config";
import { TrendingUp } from "lucide-react";
import React from "react";
import { motion } from "motion/react";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};


export default function ZumaGettingStartedSection({
  config,
}: {
  config: TCandidatesConfig["gettingStarted"];
}) {
  return (
    <div className="bg-white">
      {/* Partners Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            className="text-center mb-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2
              className="text-2xl font-semibold text-gray-900 mb-2"
              variants={ {
                hidden: { opacity: 0, y: 30 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.6,
                    ease: "easeOut",
                  },
                },
              }}
            >
              {config.ourPartners.title}
            </motion.h2>
            <motion.p className="text-gray-600" variants={ {
                    hidden: { opacity: 0, y: 30 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.6,
                        ease: "easeOut",
                      },
                    },
                  }}>
              {config.ourPartners.description}
            </motion.p>
          </motion.div>

          <motion.div
            className="flex flex-wrap justify-center items-center gap-8 md:gap-12"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            {config.partners.map((partner, index) => (
              <motion.div
                key={index}
                className="flex items-center justify-center px-6 py-3 bg-white rounded-lg shadow-sm border border-gray-200 min-w-[120px]"
                variants={{
                  hidden: { opacity: 0, scale: 0.9, y: 20 },
                  visible: {
                    opacity: 1,
                    scale: 1,
                    y: 0,
                    transition: {
                      duration: 0.5,
                      ease: "easeOut",
                    },
                  },
                  hover: {
                    scale: 1.05,
                    y: -3,
                    transition: {
                      duration: 0.2,
                      ease: "easeInOut",
                    },
                  },
                }}
                whileHover="hover"
                whileTap={{ scale: 0.98 }}
              >
                <span className="text-gray-600 font-medium text-sm whitespace-nowrap">
                  {partner.logo}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Getting Started Section */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2
              className="text-3xl lg:text-4xl font-normal text-gray-900 mb-4"
              variants={ {
                hidden: { opacity: 0, y: 30 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.6,
                    ease: "easeOut",
                  },
                },
              }}
            >
              {config.title}
            </motion.h2>
            <motion.p className="text-lg text-gray-600" variants={ {
                hidden: { opacity: 0, y: 30 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.6,
                    ease: "easeOut",
                  },
                },
              }}>
              {config.description}
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Steps Column */}
            <motion.div
              className="space-y-8"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
            >
              {config.steps.map((step, index) => (
                <motion.div
                  key={index}
                  className="flex gap-4"
                  variants={ {
                    hidden: { opacity: 0, x: -30 },
                    visible: {
                      opacity: 1,
                      x: 0,
                      transition: {
                        duration: 0.6,
                        ease: "easeOut",
                      },
                    },
                  }}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Step Number Circle */}
                  <motion.div
                    className="flex-shrink-0 w-12 h-12 bg-orange-400 rounded-full flex items-center justify-center"
                    variants={ {
                      hidden: { scale: 0, rotate: -180 },
                      visible: {
                        scale: 1,
                        rotate: 0,
                        transition: {
                          duration: 0.6,
                          ease: "easeIn",
                        },
                      },
                      hover: {
                        scale: 1.1,
                        rotate: 5,
                        transition: {
                          duration: 0.2,
                          ease: "easeInOut",
                        },
                      },
                    }}
                    whileHover="hover"
                  >
                    <span className="text-white font-semibold text-sm">
                      {step.number}
                    </span>
                  </motion.div>

                  {/* Step Content */}
                  <div className="flex-1 pt-1">
                    <motion.h3
                      className="text-xl font-semibold text-gray-900 mb-3"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 + 0.2, duration: 0.5 }}
                    >
                      {step.title}
                    </motion.h3>
                    <motion.p
                      className="text-gray-600 leading-relaxed"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 + 0.3, duration: 0.5 }}
                    >
                      {step.description}
                    </motion.p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Progress Card */}
            <motion.div
              className="lg:ml-8"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={ {
                hidden: { opacity: 0, scale: 0.9, y: 40 },
                visible: {
                  opacity: 1,
                  scale: 1,
                  y: 0,
                  transition: {
                    duration: 0.8,
                    ease: "easeOut",
                  },
                },
              }}
            >
              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-8 shadow-lg border border-orange-100">
                {/* Progress Icon */}
                <div className="flex justify-center mb-6">
                  <motion.div
                    className="w-16 h-16 bg-orange-400 rounded-full flex items-center justify-center"
                    variants={{
                      hidden: { scale: 0, rotate: -90 },
                      visible: {
                        scale: 1,
                        rotate: 0,
                        transition: {
                          duration: 0.6,
                          ease: "easeIn",
                        },
                      },
                      hover: {
                        scale: 1.1,
                        rotate: 10,
                        transition: {
                          duration: 0.2,
                          ease: "easeInOut",
                        },
                      },
                    }}
                    whileHover="hover"
                  >
                    <TrendingUp className="w-8 h-8 text-white" />
                  </motion.div>
                </div>

                {/* Card Title */}
                <motion.h3
                  className="text-xl font-semibold text-gray-900 text-center mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                >
                  {config.progressTitle}
                </motion.h3>

                {/* Progress Items */}
                <motion.div
                  className="space-y-6"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  {config.progressData.map((item, index) => (
                    <motion.div
                      key={index}
                      className="space-y-2"
                      variants={ {
                        hidden: { opacity: 0, y: 30 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.6,
                            ease: "easeOut",
                          },
                        },
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700 font-medium">
                          {item.label}
                        </span>
                        <motion.span
                          className="text-orange-600 font-bold text-lg"
                          initial={{ opacity: 0, scale: 0.5 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{
                            delay: index * 0.1 + 0.5,
                            duration: 0.4,
                            ease: "easeOut",
                          }}
                        >
                          {item.value || item.count}
                        </motion.span>
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <motion.div
                          className="bg-gradient-to-r from-orange-400 to-orange-500 h-2 rounded-full"
                          variants={ {
                            hidden: { width: "0%" },
                            visible: (progress: number) => ({
                              width: `${progress}%`,
                              transition: {
                                duration: 1.5,
                                ease: "easeOut",
                                delay: 0.3,
                              },
                            }),
                          }}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true }}
                          custom={item.progress}
                        />
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}